import { createSignal } from "solid-js"

/**
 * Loads per-session snapshots, such as run statuses, that events also update.
 * A change recorded while a snapshot request is in flight is newer than the
 * snapshot, so it is kept when the snapshot is applied.
 */
export function createSessionSnapshotLoader<V>(input: {
  load: () => Promise<Record<string, V>>
  apply: (values: Record<string, V>) => void
}) {
  let revision = 0
  let latestRequest = 0
  const changes = new Map<string, { revision: number; value: V }>()
  const [loaded, setLoaded] = createSignal(false)
  const [confirmed, setConfirmed] = createSignal(false)

  async function refreshWith<T>(
    load: () => Promise<T>,
    valuesOf: (value: T) => Record<string, V>,
    apply: (value: T, values: Record<string, V>) => void,
  ) {
    const request = ++latestRequest
    const requestRevision = revision
    try {
      const value = await load()
      if (request !== latestRequest) return false
      const values = { ...valuesOf(value) }
      for (const [sessionID, change] of changes) {
        if (change.revision > requestRevision) values[sessionID] = change.value
      }
      apply(value, values)
      for (const [sessionID, change] of changes) {
        if (change.revision <= requestRevision) changes.delete(sessionID)
      }
      setLoaded(true)
      setConfirmed(true)
      return true
    } catch (err) {
      if (request === latestRequest) setLoaded(true)
      throw err
    }
  }

  return {
    get loaded() {
      return loaded()
    },
    /**
     * A snapshot has replaced every value this page started with, which may be
     * a cache's. Unlike `loaded`, an event or a failed snapshot does not count.
     */
    get confirmed() {
      return confirmed()
    },
    changed(sessionID: string, value: V) {
      revision += 1
      changes.set(sessionID, { revision, value })
      setLoaded(true)
    },
    refresh() {
      return refreshWith(input.load, (values) => values, (_, values) => input.apply(values))
    },
    refreshWith<T>(
      load: () => Promise<T>,
      valuesOf: (value: T) => Record<string, V>,
      apply: (value: T, values: Record<string, V>) => void,
    ) {
      let current = 0
      return async () => {
        const request = ++current
        const requestRevision = revision
        try {
          const value = await load()
          if (request !== current) return false
          const values = { ...valuesOf(value) }
          for (const [sessionID, change] of changes) {
            if (change.revision > requestRevision) values[sessionID] = change.value
          }
          apply(value, values)
          setLoaded(true)
          setConfirmed(true)
          return true
        } catch (err) {
          if (request === current) setLoaded(true)
          throw err
        }
      }
    },
  }
}
